import type { MarketEdition } from '@/types';

// The prediction-market rules page, shared by every edition that has one. The Next tree
// ships this as three near-identical components
// (src/components/prediction-market-26-{ar,co,mx}-content.tsx); PORTING.md G5 makes it
// one component and moves the six diverging strings into MarketEditions
// (app/lib/market_editions.rb).
export function MarketRules({ edition }: { edition: MarketEdition }) {
  return (
    <div className="prose prose-invert max-w-none">
      <h1>
        Platanus Hack 26: {edition.city} - Reglamento del Mercado de
        Predicciones
      </h1>

      <p>
        Este documento establece el reglamento del juego de predicciones (el
        "Juego") que se llevará a cabo durante Platanus Hack: {edition.city} (el
        "Evento"), organizado por Platanus Ventures SpA ("Platanus"). El Juego
        es accesorio al Evento y complementa, sin reemplazar, las bases
        principales publicadas en{' '}
        <a href={edition.basesUrl}>{edition.basesUrl}</a>.
      </p>

      <h2>Aceptación de este reglamento</h2>

      <p>
        Participar del Juego indica que aceptas todas las reglas descritas a
        continuación. Si no estás de acuerdo, no participes del Juego. Tu
        participación en el Evento no se ve afectada por no participar del
        Juego.
      </p>

      <h2>Naturaleza del Juego</h2>

      <p>
        El Juego es un juego de destreza y entretenimiento basado en la
        capacidad de los participantes para evaluar los proyectos del Evento. No
        constituye apuesta, sorteo, lotería ni juego de azar conforme a la
        legislación aplicable. La participación es gratuita y no requiere pago,
        compra ni contraprestación de ningún tipo.
      </p>

      <h2>Bananas</h2>

      <p>
        Cada participante recibirá una asignación inicial de 1.000 "bananas",
        que son fichas internas del Juego utilizadas para registrar
        predicciones. Las bananas:
      </p>

      <ol>
        <li>No tienen valor monetario ni económico.</li>
        <li>No son convertibles en dinero, criptoactivos ni bienes.</li>
        <li>No son transferibles entre participantes ni fuera del Juego.</li>
        <li>
          Existen únicamente dentro de la plataforma del Juego y se extinguen al
          finalizar el Evento.
        </li>
      </ol>

      <h2>Reglas del Juego</h2>

      <ol>
        <li>
          <strong>Elegibilidad</strong>: solo podrán participar los hackers
          seleccionados al Evento. No podrán participar miembros del equipo de
          Platanus, integrantes del jurado, mentores con acceso a información
          del jurado, ni representantes de sponsors involucrados en la decisión
          de los premios.
        </li>
        <li>
          <strong>Mecánica</strong>: durante el período habilitado, los
          participantes podrán usar sus bananas para registrar predicciones
          sobre los resultados del Evento, según las reglas operativas que
          Platanus comunique en la plataforma del Juego.
        </li>
        <li>
          <strong>Ganador</strong>: ganará el participante que, al cierre del
          Juego, posea la mayor cantidad de bananas, conforme a las predicciones
          acertadas durante el período habilitado.
        </li>
        <li>
          <strong>Conductas prohibidas</strong>: están prohibidas la colusión
          entre participantes, el uso de cuentas múltiples, la manipulación de
          la plataforma, el intercambio de bananas por dinero o favores fuera
          del Juego, y cualquier otro comportamiento que comprometa la
          integridad del Juego. La infracción dará lugar a la descalificación
          inmediata, sin recurso.
        </li>
        <li>
          <strong>Autoridad final</strong>: Platanus es la única autoridad para
          interpretar y aplicar este reglamento, determinar al ganador y
          resolver cualquier controversia. Contra sus decisiones no procede
          recurso alguno.
        </li>
        <li>
          <strong>Suspensión o cancelación</strong>: Platanus podrá suspender,
          modificar o cancelar el Juego por fallas técnicas, abusos detectados,
          causas operativas o cualquier otra circunstancia que comprometa su
          integridad, sin obligación de entregar premio alguno en tales casos.
        </li>
        <li>
          <strong>Sin garantía técnica</strong>: la plataforma del Juego se
          provee "tal cual" y sin garantía de disponibilidad continua. Errores
          técnicos, caídas o pérdida de datos no darán derecho a indemnización.
        </li>
      </ol>

      <h2>Premios</h2>

      <ol>
        <li>
          <strong>Premio</strong>: el ganador del Juego recibirá un premio en
          dinero y/o criptoactivos, cuya composición, monto y condiciones
          específicas serán comunicados por Platanus oportunamente. Variaciones
          respecto de comunicaciones previas no generan responsabilidad alguna
          para Platanus.
        </li>
        <li>
          <strong>Pago en criptoactivos</strong>: si el premio se entrega total
          o parcialmente en criptoactivos, el ganador acepta el riesgo de
          volatilidad, custodia y conversión, y se compromete a proveer una
          billetera compatible. Platanus no actúa como proveedor de servicios de
          activos virtuales{edition.virtualAssetProviderNote} y la entrega del
          criptoactivo podrá realizarse directamente por el sponsor responsable.
        </li>
        <li>
          <strong>Impuestos</strong>: los premios pueden estar sujetos a
          impuestos a cargo del ganador, incluyendo, cuando corresponda,{' '}
          {edition.taxNote}. Platanus practicará las retenciones que la ley
          exija.
        </li>
        <li>
          <strong>Identificación del ganador</strong>: para recibir el premio,
          el ganador deberá entregar a Platanus los datos de identificación,
          fiscales y de pago (cuenta bancaria o billetera de criptoactivos,
          según corresponda) que sean necesarios. La negativa a entregarlos en
          un plazo razonable habilita a Platanus a declarar desierto el premio.
        </li>
        <li>
          <strong>Responsabilidad por los premios</strong>: la responsabilidad
          de Platanus respecto de los premios a cargo de sponsors se limita a
          transmitirles la instrucción de entrega. Platanus no garantiza la
          conducta de los sponsors. Esta limitación no aplica respecto de los
          premios que sean de cargo de Platanus.
        </li>
      </ol>

      <h2>Datos personales</h2>

      <p>
        Los datos que entregues con motivo del Juego se tratarán bajo la misma
        política de privacidad de las bases del Evento. Conservas todos los
        derechos que la legislación de protección de datos personales aplicable
        te ofrece, incluida {edition.dataProtectionLaw}, y podrás ejercerlos por
        comunicación al correo electrónico{' '}
        <a href="mailto:hack@platan.us">hack@platan.us</a>.
      </p>

      <h2>Solución de controversias y ley aplicable</h2>

      <p>
        Cualquier controversia relativa a este reglamento se resolverá conforme
        al procedimiento previsto en las bases del Evento. Este reglamento se
        rige por las leyes de {edition.governingLaw} respecto de los
        participantes con residencia en {edition.residence}; en lo demás, por
        las leyes de la República de Chile, sin perjuicio de las normas
        imperativas y de orden público que resulten aplicables.
      </p>

      <p>
        La nulidad o ineficacia de cualquier disposición de este reglamento no
        afectará la validez de las restantes.
      </p>
    </div>
  );
}
